import API from "helpers/api";
import { Apis } from "services/api";
import { getAPIResponse } from "helpers/getApiResponse";

// GET /api/localisation/bundle?locale=<tag>
// Returns { locale, etag, messages } where messages is a flat map of
// dotted keys to either a plain string or an object keyed by CLDR plural
// category ({ one: "Asset", other: "Assets" }). Anonymous calls get shipped
// defaults; a bearer token scopes the bundle to the caller's site app.
export const getLocalisationBundle = async (locale = "en") => {
	try {
		const response = await API.get(`${Apis.Localisation}/bundle`, {
			params: { locale },
		});
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// GET /api/localisation/locales
// Returns the locales this build ships a language file for, e.g. ["en", "zh"].
export const getAvailableLocales = async () => {
	try {
		const response = await API.get(`${Apis.Localisation}/locales`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// GET /api/localisation/captions?locale=<tag>
// Returns the CustomCaption DTO ({ asset, assetPlural, … }) resolved for the
// caller's site in the requested locale — the authoritative localised captions.
export const getLocalisedCaptions = async (locale = "en") => {
	try {
		const response = await API.get(`${Apis.Localisation}/captions`, {
			params: { locale },
		});
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// GET /api/localisation/preference
// Returns { locale } — the caller's own preferred locale, or null if unset.
export const getUserLocalePreference = async () => {
	try {
		const response = await API.get(`${Apis.Localisation}/preference`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// PUT /api/localisation/preference — sets the caller's preferred locale.
export const setUserLocalePreference = async (locale) => {
	try {
		const response = await API.put(`${Apis.Localisation}/preference`, {
			locale,
		});
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// GET /api/localisation/users/{userId}/preference
// Returns { locale } — the target user's preferred locale, or null if unset.
export const getUserPreferenceFor = async (userId) => {
	try {
		const response = await API.get(
			`${Apis.Localisation}/users/${userId}/preference`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// PUT /api/localisation/users/{userId}/preference — sets the target user's
// preferred locale.
export const setUserPreferenceFor = async (userId, locale) => {
	try {
		const response = await API.put(
			`${Apis.Localisation}/users/${userId}/preference`,
			{ locale }
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// GET /api/localisation/settings
// Returns { defaultLocale, enabledLocales } for the caller's current site,
// keyed off the SiteAppID claim (400 outside a site app context).
export const getLocaleSettings = async () => {
	try {
		const response = await API.get(`${Apis.Localisation}/settings`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// GET /api/localisation/keys?locale=<tag>[&siteAppId=<id>]
// Returns [{ key, defaults }] — the canonical key set (from en.po) with the
// requested locale's shipped default forms (en fallback for untranslated keys).
// defaults is a string array: one element for a single-form locale/term,
// [singular, plural] for a two-form one. For a siteapp scope the siteApp id is
// sent so defaults are the values that siteApp inherits (parent Application and
// Client overrides layered over the shipped forms).
export const getLocalisationKeys = async (locale = "en", scope = null) => {
	try {
		const params = { locale };
		if (scope?.level === "siteapp" && scope?.id) {
			params.siteAppId = scope.id;
		}
		const response = await API.get(`${Apis.Localisation}/keys`, { params });
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// GET /api/localisation/sites/{siteId}/settings
// Returns { defaultLocale, enabledLocales } for the given site (admin/client-admin only).
export const getSiteLocaleSettings = async (siteId) => {
	try {
		const response = await API.get(`${Apis.Localisation}/sites/${siteId}/settings`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// PUT /api/localisation/sites/{siteId}/settings
// Updates the site's default locale and enabled-locales whitelist.
export const updateSiteLocaleSettings = async (siteId, { defaultLocale, enabledLocales }) => {
	try {
		const response = await API.put(`${Apis.Localisation}/sites/${siteId}/settings`, {
			defaultLocale,
			enabledLocales,
		});
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// The overrides endpoint for an inheritance level. No scope = the caller's own
// site app (tenant); { level: "application" | "client", id } targets the
// super-admin template or a client-wide override set.
const overridesUrl = (scope) => {
	if (scope?.level === "application")
		return `${Apis.Localisation}/applications/${scope.id}/overrides`;
	if (scope?.level === "client")
		return `${Apis.Localisation}/clients/${scope.id}/overrides`;
	if (scope?.level === "siteapp")
		return `${Apis.Localisation}/siteapps/${scope.id}/overrides`;
	return `${Apis.Localisation}/overrides`;
};

// GET overrides for a scope: [{ key, locale, pluralCategory, value }];
// pluralCategory is null for non-plural terms.
export const getLocalisationOverrides = async (locale, scope) => {
	try {
		const response = await API.get(overridesUrl(scope), {
			params: locale ? { locale } : {},
		});
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// PUT overrides for a scope. forms is keyed by CLDR plural category
// ("one"/"other"/…) or "" for non-plural terms; null/empty values remove that
// form's override. The site-app scope returns the rebuilt bundle; the
// application/client scopes return the updated override list.
export const upsertLocalisationOverride = async ({ key, locale, forms, scope }) => {
	try {
		const response = await API.put(overridesUrl(scope), {
			key,
			locale,
			forms,
		});
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

// DELETE overrides for a scope by key + locale (removes every form for the term).
export const deleteLocalisationOverride = async (key, locale, scope) => {
	try {
		const response = await API.delete(overridesUrl(scope), {
			params: { key, locale },
		});
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
